import { ActionType } from '@/store/slices/editor/actionTypes';

export const panelListItems = [
  {
    id: 'templates',
    name: 'Templates',
  },
  {
    id: 'ai-image',
    name: 'AI Image',
    disable: false,
  },
  {
    id: 'photos',
    name: 'Photos',
    disable: false,
  },
  {
    id: 'text',
    name: 'Text',
    disable: false,
  },
  {
    id: 'stock',
    name: 'Stock',
    disable: false,
  },
  // {
  //   id: 'image',
  //   name: 'Images',
  // },
  // {
  //   id: 'uploads',
  //   name: 'Uploads',
  // },
  // {
  //   id: 'background',
  //   name: 'Background',
  //   disable: false,
  // },
  // {
  //   id: 'vectors',
  //   name: 'Vectors',
  //   disable: false,
  // },
  // {
  //   id: 'overlay',
  //   name: 'Overlay',
  //   disable: false,
  // },
  {
    id: 'graphics',
    name: 'Graphics',
    disable: false,
  },
  // {
  //   id: 'pixabay',
  //   name: 'Pixabay',
  // },
]

export enum PanelType {
  // TEMPLATES = 'Templates',
  BACKGROUND = 'Background',
  TOOLBOX = 'Toolbox',
}

export enum PopoverType {
  PHOTOS = 'PHOTOS',
  STOCK = 'STOCK',
  GRAPHICS = 'GRAPHICS',
  TEMPLATES = 'TEMPLATES',
  TEXT = 'TEXT',
  AI_IMAGE = 'AI_IMAGE',
}

export interface DeeplinkPostAction {
  type: 'open_inspector'
  inspectorType: string
  selectedId?: string
}

export interface PopoverActiveState {
  type: PopoverType
  selectedId?: string
  subCategory?: string
  postAction?: DeeplinkPostAction
}

/**
 * Expected durations (s) used to pace the percentage loader for generation
 * operations. Slower operations (4K render, video) get a longer ramp so the
 * percentage tracks the real wait instead of racing to the plateau.
 */
export const GENERATION_LOADER_SECONDS = {
  image: 25,
  hd: 30,
  fourK: 45,
  video: 60,
} as const

export enum CanvasOverlayState {
  none,
  loading,
  transparent,
  dark,
  gradientFrame,
  gradientFrameNoText,
  gradientObject,
  gradientObjectNoText,
  gradientFrameWithPercentage,
  gradientFrameNoTextWithPercentage,
  gradientObjectWithPercentage,
  gradientObjectNoTextWithPercentage,
}

export interface DisableEditorState {
  disableInspector: boolean;
  addMenu: 'active' | 'disabled' | 'hidden';
  canvasOverlay: CanvasOverlayState;
  disableNavbar: boolean;
  hideTooltip: boolean;
  history: 'active' | 'disabled';
  zoom: 'active' | 'disabled';
  disableHotkey: boolean;
  scenes: 'active' | 'disabled' | 'hidden';
  floatingInspector: 'active' | 'disabled' | 'hidden' | 'hidden-preserve-space';
  editWithAI: 'active' | 'disabled' | 'hidden' | 'hidden-preserve-space';
  loaderExpectedDurationSeconds?: number;
};

export type FloatingInspectorButtonSet = 'aiFashion' | 'photoshoot' | null
export type SuggestionsButtonSet = 'aiFashion' | 'ghost' | 'photoshoot' | null
export type NavbarButtonSet = 'aiFashion' | 'photoshoot' | null

export type LoaderTextAnimationType = 'fixedWidthTextSliding' | 'widthContentFit'

export interface LoaderTextState {
  texts: string[]
  style: 'gradient'
  minWidth?: string
  animationType?: LoaderTextAnimationType
}

export interface CanvasInteractionState {
  doubleClickOnActiveObject: 'openCrop' | 'none'
}

export const defaultCanvasInteractionState: CanvasInteractionState = {
  doubleClickOnActiveObject: 'openCrop',
}

export interface EditorUIState {
  floatingInspector: 'activeObject' | 'customButtonSet'
  floatingInspectorButtonSet: FloatingInspectorButtonSet
  suggestions: 'aiEditSuggestions' | 'customButtonSet'
  suggestionsButtonSet: SuggestionsButtonSet
  scenes: 'download' | 'scenes'
  scenesGeneration: 'current' | 'consecutive' | 'none'
  navbar: 'editor' | 'customButtonSet'
  navbarButtonSet: NavbarButtonSet
  loaderText: LoaderTextState | null
  editWithAiLoaderLabel?: string | null
  editWithAIState?: 'default' | 'loading'
}

export const defaultEditorUIState: EditorUIState = {
  floatingInspector: 'activeObject',
  floatingInspectorButtonSet: null,
  suggestions: 'aiEditSuggestions',
  suggestionsButtonSet: null,
  scenes: 'scenes',
  navbar: 'editor',
  navbarButtonSet: null,
  loaderText: null,
  scenesGeneration: 'none',
}

/**
 * UI state used while the (canvas-less) `/create/photoshoots` run step is
 * mounted. Only the Navbar-relevant fields matter here — the rest of the
 * editor surfaces (floating inspector, suggestions) never render on this
 * route, so their values are kept compatible but inert.
 */
export const defaultPhotoshootUIState: EditorUIState = {
  floatingInspector: 'activeObject',
  floatingInspectorButtonSet: 'photoshoot',
  suggestions: 'aiEditSuggestions',
  suggestionsButtonSet: null,
  scenes: 'scenes',
  navbar: 'customButtonSet',
  navbarButtonSet: 'photoshoot',
  loaderText: null,
  scenesGeneration: 'none',
}

// Define the default value with all properties set to true
export const defaultEnabledEditorState: DisableEditorState = {
  disableInspector: false,
  addMenu: 'active',
  canvasOverlay: CanvasOverlayState.none,
  disableNavbar: false,
  hideTooltip: false,
  history: 'active',
  zoom: 'active',
  disableHotkey: false,
  scenes: 'active',
  floatingInspector: 'active',
  editWithAI: 'active'
};

export const defaultEnabledPixelEditorState: DisableEditorState = {
  disableInspector: false,
  addMenu: 'hidden',
  canvasOverlay: CanvasOverlayState.none,
  disableNavbar: false,
  hideTooltip: false,
  history: 'active',
  zoom: 'active',
  disableHotkey: false,
  scenes: 'hidden',
  floatingInspector: 'hidden',
  editWithAI: 'hidden',
};

export const defaultDisabledEditorState: DisableEditorState = {
  disableInspector: true,
  addMenu: 'disabled',
  canvasOverlay: CanvasOverlayState.none,
  disableNavbar: true,
  hideTooltip: true,
  history: 'disabled',
  zoom: 'disabled',
  disableHotkey: true,
  scenes: 'disabled',
  floatingInspector: 'hidden',
  editWithAI: 'disabled'
};

export const defaultAIFlowEntryState: DisableEditorState = {
  disableInspector: true,
  addMenu: 'hidden',
  canvasOverlay: CanvasOverlayState.gradientFrameNoTextWithPercentage,
  disableNavbar: false,
  hideTooltip: true,
  history: 'disabled',
  zoom: 'disabled',
  disableHotkey: true,
  scenes: 'disabled',
  floatingInspector: 'disabled',
  editWithAI: 'disabled',
};

export const defaultAIFlowReadyState: DisableEditorState = {
  disableInspector: true,
  addMenu: 'hidden',
  canvasOverlay: CanvasOverlayState.none,
  disableNavbar: false,
  hideTooltip: true,
  history: 'active',
  zoom: 'active',
  disableHotkey: true,
  scenes: 'active',
  floatingInspector: 'active',
  editWithAI: 'active',
};

/** Returns the initial DisableEditorState for the editor mount. */
export function getFirstDisabledEditorState(_callbackFromCreatePageActionType: ActionType | null | undefined): DisableEditorState {
  return defaultEnabledEditorState;
}

/** Returns the initial EditorUIState for the editor mount. */
export function getFirstEditorState(_callbackFromCreatePageActionType: ActionType | null | undefined): EditorUIState {
  return defaultEditorUIState
}
